import { defineStore, storeToRefs } from "pinia"
import { useInstructionStore } from "@/store/instructions.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { RentalDispense } from "@/models/rental-dispense.model"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { type Instruction } from "@/models/instruction.model"
import { computed, type Ref, ref } from "vue"

export const useRentalDispenseStore = defineStore("rentalDispenseStore", () => {
  const instructionStore = useInstructionStore()
  const { instructions } = storeToRefs(instructionStore)

  const rentalDispenses = computed(() => {
    const rentalDispenses = []
    for (const instruction of instructions.value) {
      rentalDispenses.push(...instruction.rentalDispenses)
    }
    return rentalDispenses
  })
  // Rental dispenses that have been retrieved for the purpose of returning
  const retrievedRentalDispenses: Ref<{ [key: string]: RentalDispense }> = ref({})

  const returnableRentalDispenses = computed(() => {
    const combined = Object.values(retrievedRentalDispenses.value)
    for (const rentalDispense of rentalDispenses.value) {
      if (!retrievedRentalDispenses.value[rentalDispense.id]) {
        combined.push(rentalDispense)
      }
    }

    return combined.filter(
      (rentalDispense) => rentalDispense.isIssuance() && rentalDispense.getRemainingQuantity() > 0
    )
  })

  function addToReturnableRentalDispenses(newRentalDispenses: RentalDispense[]) {
    for (const rentalDispense of newRentalDispenses) {
      retrievedRentalDispenses.value[rentalDispense.id] = rentalDispense
    }
  }

  const { pushToRelatedObjects, removeFromRelatedObjects } = useRelatedObjectsManager(
    instructions,
    (rentalDispense: RentalDispense) => rentalDispense.instruction,
    (instruction: Instruction) => instruction.id!,
    (instruction: Instruction) => instruction.rentalDispenses
  )
  function removeRentalDispense(rentalDispense: RentalDispense) {
    removeFromRelatedObjects(rentalDispense)
  }

  useSocketSynchronizationHandlers(RentalDispense, null, pushToRelatedObjects, removeRentalDispense)

  return {
    rentalDispenses,
    returnableRentalDispenses,
    pushToRelatedObjects,
    removeFromRelatedObjects: removeRentalDispense,
    addToReturnableRentalDispenses
  }
})
